import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Axios from 'axios';
import Compressor from 'compressorjs';
import { Helmet } from 'react-helmet-async';

import { UserContext, SnackbarContext } from './userContext';
import { BackDropLoad } from './LoadingBackDrop';
import {
  PageContainer,
  PageHero,
  Section,
  EmptyState,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const I = {
  Upload: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Spark: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m12 3 2 7 7 2-7 2-2 7-2-7-7-2 7-2z" />
    </svg>
  ),
};

function Field({ label, value, onChange, error, errorMsg, multiline, rows = 5, required, placeholder, type = 'text' }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <div
        className={`
          relative flex items-start gap-2 rounded-lg
          ${multiline ? 'min-h-[120px] p-3' : 'h-11 px-3 items-center'}
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          focus-within:bg-surface-overlay
          focus-within:shadow-[0_0_0_3px_rgba(251,191,36,0.18)]
          ${error
            ? 'border-red-400/55 focus-within:border-red-400'
            : 'border-border-subtle hover:border-amber/40 focus-within:border-amber'}
        `}
      >
        <Tag
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={multiline ? rows : undefined}
          className={`
            w-full bg-transparent
            text-[14px] font-medium text-fg-primary
            placeholder:text-fg-muted
            outline-none border-0 p-0
            ${multiline ? 'leading-[1.6] resize-y min-h-[100px]' : 'leading-none'}
          `}
        />
      </div>
      <div className="mt-1.5 min-h-[14px] text-[11px] leading-[1.4]">
        {error && <span className="text-red-400">{errorMsg}</span>}
      </div>
    </div>
  );
}

export const QandA = () => {
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);

  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(0);
  const [wait, setWait] = useState(false);

  const [quesTitle, setQuesTitle] = useState('');
  const [quesDesc, setQuesDesc] = useState('');
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const mRes = await Axios.post('/new_apis/getsinglemetadata', { type: 'about' }, { headers: { key } });
        if (mRes.data.status) setMetadata(mRes.data.metadata[0]);
        else setMetadata({ seo_title: 'Community Q&A | ESAY' });
      } catch { /* noop */ }

      try {
        const qRes = await Axios.get('/new_apis/get_all_questions', { headers: { key } });
        if (qRes.data.status === 1) setData(qRes.data.data || []);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  const compressImage = (image) =>
    new Promise((resolve) => {
      new Compressor(image, {
        quality: 0.8,
        success: (compressed) => resolve(compressed),
        error: () => resolve(image),
      });
    });

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > 3) {
      setSnack({ message: 'Maximum 3 images allowed.', type: 'warning', open: true });
      e.target.value = '';
      return;
    }
    const added = [];
    for (const f of files) {
      if (['image/jpeg', 'image/jpg', 'image/png'].includes(f.type)) {
        // eslint-disable-next-line no-await-in-loop
        const compressed = await compressImage(f);
        added.push({ raw: compressed, preview: URL.createObjectURL(compressed) });
      }
    }
    setImages((prev) => [...prev, ...added]);
    e.target.value = '';
  };

  const removeImg = (preview) => setImages((prev) => prev.filter((i) => i.preview !== preview));

  const submit = (e) => {
    e.preventDefault();
    setError(0);
    if (!quesTitle) {
      setError(1);
      setSnack({ message: 'Please enter a question title.', type: 'error', open: true });
      return;
    }
    if (!quesDesc) {
      setError(2);
      setSnack({ message: 'Please enter a question description.', type: 'error', open: true });
      return;
    }
    setWait(true);
    const formData = new FormData();
    formData.append('user_id', user?.user_id || '');
    formData.append('question_title', quesTitle);
    formData.append('question_desc', quesDesc);
    if (videoUrl) formData.append('video_url', videoUrl);
    images.forEach((img) => formData.append('images', img.raw));

    Axios.post('/new_apis/ask_question', formData, { headers: { tkn: user?.token } }).then((res) => {
      const s = res.data.status;
      if (s === 1) {
        setWait(false);
        setImages([]); setQuesTitle(''); setQuesDesc(''); setVideoUrl('');
        setSnack({ message: res.data.msg, type: 'success', open: true });
      } else if (s === 2) {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      } else if (s === 3) {
        setWait(false);
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true });
      } else if (s === 4) {
        setUser(null);
        setWait(false);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true });
      }
    });
  };

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          {metadata.seo_desc && <meta name="description" content={metadata.seo_desc} />}
        </Helmet>
      )}

      <BackDropLoad wait={wait} />

      <PageContainer maxWidth="lg">
        <PageHero
          compact
          kicker="Community"
          title="Q&A"
          subtitle="Your space for questions and collaborative answers from the ESAY community."
        />

        <Section>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-4 md:gap-6">
            {/* Form */}
            <div className="
              relative overflow-hidden rounded-2xl
              border border-border-subtle bg-surface-paper
              p-4 sm:p-6
            ">
              <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/40 to-transparent" />
              <div className="mb-1 inline-flex items-center gap-1.5">
                <I.Spark className="h-3 w-3 text-amber" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Ask</span>
              </div>
              <h2 className="text-[18px] sm:text-[20px] font-bold tracking-[-0.018em] text-fg-primary">
                Ask a question
              </h2>
              <p className="mt-1 text-[13px] leading-[1.6] text-fg-secondary">
                Add context and anything that would help others answer you.
              </p>

              <form onSubmit={submit} noValidate className="mt-4 grid grid-cols-1 gap-3">
                <Field
                  label="Question title"
                  value={quesTitle}
                  onChange={(e) => setQuesTitle(e.target.value)}
                  error={error === 1}
                  errorMsg="Please enter a question title."
                  required
                />
                <Field
                  label="Question description"
                  multiline
                  rows={6}
                  value={quesDesc}
                  onChange={(e) => setQuesDesc(e.target.value)}
                  error={error === 2}
                  errorMsg="Please enter a description."
                  required
                />

                <div>
                  <input
                    id="qa-upload"
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={onFileChange}
                    hidden
                  />
                  <label
                    htmlFor="qa-upload"
                    className="
                      inline-flex h-11 items-center gap-1.5 rounded-lg px-4 cursor-pointer
                      text-[11.5px] font-bold uppercase tracking-[0.1em]
                      border border-amber/40 bg-amber/10 text-amber
                      transition-[background-color,border-color] duration-200
                      hover:bg-amber/20 hover:border-amber/60
                    "
                  >
                    <I.Upload className="h-4 w-4" />
                    Upload images (max 3)
                  </label>

                  {images.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {images.map((img) => (
                        <div
                          key={img.preview}
                          className="relative overflow-hidden rounded-lg border border-border-subtle bg-surface-overlay aspect-square"
                        >
                          <img src={img.preview} alt="" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImg(img.preview)}
                            aria-label="Remove image"
                            className="
                              absolute top-1.5 right-1.5
                              inline-flex h-7 w-7 items-center justify-center rounded-full
                              bg-black/72 backdrop-blur-md text-fg-primary
                              transition-[background-color,color] duration-200
                              hover:bg-red-400 hover:text-ink
                            "
                          >
                            <I.Close className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Field
                  label="Video URL (optional)"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/..."
                />

                <button
                  type="submit"
                  disabled={wait}
                  className="
                    group inline-flex h-12 w-full items-center justify-center gap-1.5 rounded-lg px-5
                    text-[12.5px] font-bold uppercase tracking-[0.1em]
                    bg-amber text-ink
                    transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                    hover:bg-amber-light
                    hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                    active:scale-[0.98]
                    disabled:opacity-60 disabled:pointer-events-none
                  "
                >
                  <span>Submit question</span>
                  <I.ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </form>
            </div>

            {/* Sidebar (hidden on mobile) */}
            <aside className="
              hidden md:block sticky top-[68px] self-start
              rounded-2xl border border-amber/24 bg-amber/5 p-3
              max-h-[calc(100vh-92px)] overflow-y-auto
            ">
              <div className="mb-3 px-1.5 flex items-center gap-1.5">
                <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Trending questions</span>
              </div>
              {loading ? null : data.length === 0 ? (
                <EmptyState title="No questions yet" subtitle="Be the first to ask." />
              ) : (
                <ul className="flex flex-col gap-1">
                  {data.slice(0, 8).map((q, i) => (
                    <li
                      key={i}
                      className="
                        rounded-lg px-2.5 py-2
                        text-[12.5px] leading-[1.5] text-fg-secondary
                        transition-colors duration-200
                        hover:bg-amber/10 hover:text-fg-primary
                      "
                    >
                      {q.question_title || 'Question'}
                    </li>
                  ))}
                </ul>
              )}
            </aside>
          </div>
        </Section>
      </PageContainer>
    </>
  );
};
